import { forwardRef, useState, type InputHTMLAttributes } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = 'text',
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      id,
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')
    const [isFocused, setIsFocused] = useState(false)

    return (
      <div className="w-full">
        {label && (
          <motion.label
            htmlFor={inputId}
            className="block text-sm font-medium text-neutral-300 mb-1.5"
            animate={{
              color: isFocused ? 'rgb(0, 217, 255)' : 'rgb(203, 213, 224)',
            }}
            transition={{ duration: 0.2 }}
          >
            {label}
          </motion.label>
        )}
        <div className="relative">
          {leftIcon && (
            <motion.div
              className="absolute left-3 top-1/2 -translate-y-1/2"
              animate={{
                color: isFocused ? 'rgb(0, 217, 255)' : 'rgb(160, 174, 192)',
              }}
              transition={{ duration: 0.2 }}
            >
              {leftIcon}
            </motion.div>
          )}
          <motion.div
            className="relative"
            animate={{
              boxShadow: isFocused
                ? '0 0 0 3px rgba(0, 217, 255, 0.15)'
                : '0 0 0 0px rgba(0, 217, 255, 0)',
            }}
            transition={{ duration: 0.2 }}
            style={{ borderRadius: '0.75rem' }}
          >
            <input
              ref={ref}
              id={inputId}
              type={type}
              className={cn(
                'w-full px-4 py-2.5 rounded-xl',
                'bg-primary-mid/40 backdrop-blur-sm',
                'border border-white/10',
                'text-neutral-100 placeholder:text-neutral-500',
                'transition-all duration-200',
                'hover:border-white/20',
                'focus:outline-none focus:border-primary-accent',
                error && 'border-secondary-pink focus:border-secondary-pink',
                leftIcon && 'pl-10',
                rightIcon && 'pr-10',
                className
              )}
              onFocus={(e) => {
                setIsFocused(true)
                onFocus?.(e)
              }}
              onBlur={(e) => {
                setIsFocused(false)
                onBlur?.(e)
              }}
              {...props}
            />
          </motion.div>
          {rightIcon && (
            <motion.div
              className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
              animate={{
                color: isFocused ? 'rgb(0, 217, 255)' : 'rgb(160, 174, 192)',
              }}
              transition={{ duration: 0.2 }}
            >
              {rightIcon}
            </motion.div>
          )}
        </div>

        {/* Animated error/hint messages */}
        <AnimatePresence mode="wait">
          {error && (
            <motion.p
              key="error"
              className="mt-1.5 text-sm text-secondary-pink"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
            >
              {error}
            </motion.p>
          )}
          {hint && !error && (
            <motion.p
              key="hint"
              className="mt-1.5 text-sm text-neutral-500"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
            >
              {hint}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    )
  }
)

Input.displayName = 'Input'

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  hint?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, hint, id, onFocus, onBlur, ...props }, ref) => {
    const textareaId = id || label?.toLowerCase().replace(/\s+/g, '-')
    const [isFocused, setIsFocused] = useState(false)

    return (
      <div className="w-full">
        {label && (
          <motion.label
            htmlFor={textareaId}
            className="block text-sm font-medium text-neutral-300 mb-1.5"
            animate={{
              color: isFocused ? 'rgb(0, 217, 255)' : 'rgb(203, 213, 224)',
            }}
            transition={{ duration: 0.2 }}
          >
            {label}
          </motion.label>
        )}
        <motion.div
          className="relative"
          animate={{
            boxShadow: isFocused
              ? '0 0 0 3px rgba(0, 217, 255, 0.15)'
              : '0 0 0 0px rgba(0, 217, 255, 0)',
          }}
          transition={{ duration: 0.2 }}
          style={{ borderRadius: '0.75rem' }}
        >
          <textarea
            ref={ref}
            id={textareaId}
            className={cn(
              'w-full px-4 py-2.5 rounded-xl',
              'bg-primary-mid/40 backdrop-blur-sm',
              'border border-white/10',
              'text-neutral-100 placeholder:text-neutral-500',
              'transition-all duration-200',
              'hover:border-white/20',
              'focus:outline-none focus:border-primary-accent',
              'resize-none min-h-[100px]',
              error && 'border-secondary-pink focus:border-secondary-pink',
              className
            )}
            onFocus={(e) => {
              setIsFocused(true)
              onFocus?.(e)
            }}
            onBlur={(e) => {
              setIsFocused(false)
              onBlur?.(e)
            }}
            {...props}
          />
        </motion.div>

        {/* Animated error/hint messages */}
        <AnimatePresence mode="wait">
          {error && (
            <motion.p
              key="error"
              className="mt-1.5 text-sm text-secondary-pink"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
            >
              {error}
            </motion.p>
          )}
          {hint && !error && (
            <motion.p
              key="hint"
              className="mt-1.5 text-sm text-neutral-500"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
            >
              {hint}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'
